import { Grid, IconButton, TextField, makeStyles } from '@material-ui/core';
import { Close, Search } from '@material-ui/icons';
import { Colors } from '@rentguru/commons-utils';
import clsx from 'clsx';
import { debounce, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { IntlFormatters } from 'react-intl';

import { stopPropagation } from '../../../utils/eventUtils';

interface StylesProps {
  width: number;
}

const useStyles = makeStyles(() => ({
  rootText: {
    color: Colors.SLATE_GREY,
    borderBottom: `0.5px solid  ${Colors.TOWER_GREY}`,
    '&:before': {
      borderBottom: `0.5px solid  ${Colors.TOWER_GREY}`,
    },
    '&:after': {
      borderBottom: `0.5px solid  ${Colors.TOWER_GREY}`,
    },
    '&:not(.Mui-disabled):hover::before': {
      borderBottom: `0.5px solid  ${Colors.TOWER_GREY}`,
    },
    '&:focus': {
      borderBottom: `0.5px solid  ${Colors.TOWER_GREY}`,
    },
  },
  searchIconStyle: {
    fill: Colors.SLATE_GREY,
    paddingRight: 10,
    backgroundColor: Colors.CLASSICAL_WHITE,
  },
  searchField: {
    width: ({ width }: StylesProps) => width,
  },
  iconStyle: {
    height: 20,
    width: 20,
  },
  towerGreyColor: {
    color: Colors.TOWER_GREY,
  },
}));

interface SearchBarSearchProps {
  nameFilter?: string;
  width?: number;
  placeholder?: string;
  setNameFilter: (name: string) => void;
  formatMessage: IntlFormatters['formatMessage'];
}

export const SearchBar = ({
  nameFilter,
  width = 300,
  placeholder,
  setNameFilter,
  formatMessage,
}: SearchBarSearchProps) => {
  const classes = useStyles({ width });
  const [localValue, setLocalValue] = useState(nameFilter ?? '');

  useEffect(() => {
    setLocalValue(nameFilter ?? '');
  }, [nameFilter]);

  const debouncedSetNameFilter = useMemo(() => debounce((value: string) => setNameFilter(value), 250), [setNameFilter]);

  useEffect(() => () => debouncedSetNameFilter.cancel(), [debouncedSetNameFilter]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalValue(value);
    debouncedSetNameFilter(value);
  };

  const handleClear = (event: React.MouseEvent) => {
    stopPropagation(event);
    debouncedSetNameFilter.cancel();
    setLocalValue('');
    setNameFilter('');
  };

  return (
    <TextField
      variant="standard"
      id="searchOnFileName"
      size="small"
      placeholder={placeholder ?? formatMessage({ id: 'folders.searchBar.search' })}
      value={localValue}
      className={classes.searchField}
      onChange={handleChange}
      InputLabelProps={{
        style: { color: Colors.TOWER_GREY },
      }}
      InputProps={{
        startAdornment: <Search id="searchIcon" className={classes.searchIconStyle} />,
        endAdornment: isEmpty(localValue) ? undefined : (
          <Grid
            container
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            alignContent="flex-end"
            style={{ width: 70 }}
          >
            <IconButton onClick={handleClear} className={clsx(classes.iconStyle, classes.towerGreyColor)}>
              <Close />
            </IconButton>
          </Grid>
        ),
        classes: {
          root: classes.rootText,
        },
      }}
    />
  );
};
